import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toChemMoleculeV1 } from '@peruselab/structure-editor/domain/moleculeProjection';
import { computeMolecularFormula } from '@peruselab/structure-editor/chemistry/moleculeFormula';
import { FormulaText } from '../../components/FormulaText';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';

interface DocumentBarProps {
  readonly doc: UseDrawingDocumentResult;
}

/**
 * #648 -- the Markush tools' short-lived message (e.g. "only a terminal atom can carry an R
 * label"). #807 R3: shown in the paper toolbar row, next to the paper it is about, rather than in
 * the top bar with the formula -- a message appearing there would grow the bar and move the paper.
 */
export function MarkushNotice({ message }: { readonly message: string | null }) {
  return (
    <output className="workspace-markush-notice" aria-live="polite" data-testid="markush-notice">
      {message ?? ''}
    </output>
  );
}

// ADR 0007 P5 — the document strip on the right of the toolbar row. Formula readout only; the
// filter toggle that used to live here was a plain duplicate of the right panel's own FILTER tab
// (#491) and Lock/Delete/pager moved behind the action bar's `⋯` menu (UI polish review #3).
//
// #491 — renders nothing at all (not a `—` placeholder) until there's both a structure *and* a
// computed formula for it: an empty bordered pill sitting in the toolbar on a blank canvas read
// as clutter, and the formula is cheap enough to compute that a brief "nothing, then the answer"
// reads better than "placeholder, then the answer".
export function DocumentBar({ doc }: DocumentBarProps) {
  const { t } = useTranslation();
  const document = doc.document;
  const hasNodes = document.structure.graph.nodes.length > 0 && !doc.hasMarkushContent;

  const [formula, setFormula] = useState<string | null>(null);
  useEffect(() => {
    if (!hasNodes) {
      setFormula(null);
      return;
    }
    let cancelled = false;
    computeMolecularFormula(toChemMoleculeV1(document))
      .then((computedFormula) => {
        if (!cancelled) setFormula(computedFormula);
      })
      .catch(() => {
        if (!cancelled) setFormula(null);
      });
    return () => {
      cancelled = true;
    };
  }, [document, hasNodes]);

  // #648 -- a Markush drawing shows the Markush chip instead of a formula: with R atoms removed
  // the formula would describe a different molecule, and with them counted as carbon a wrong one.
  // #674: no exit -- it is a Markush drawing only while it has a variable position; undo (or
  // removing the labels) makes it a plain one again.
  if (doc.hasMarkushContent) {
    return (
      <div
        className="workspace-document-bar workspace-markush-chip"
        data-testid="markush-mode-chip"
        role="group"
        aria-label={t('workspace.documentBar.markush.mode')}
      >
        <span className="workspace-formula">{t('workspace.documentBar.markush.mode')}</span>
      </div>
    );
  }

  if (formula === null) return null;

  return (
    <div
      className="workspace-document-bar"
      data-testid="document-bar"
      role="group"
      aria-label={t('workspace.documentBar.label')}
    >
      {formula !== null ? (
        <span
          className="workspace-formula"
          data-testid="document-formula"
          title={formula}
          aria-label={t('workspace.documentBar.formula')}
        >
          <FormulaText formula={formula} />
        </span>
      ) : null}
    </div>
  );
}
